import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { CameraInfo, FrigateEvent, TriggeredAlertPayload } from '../types';
import {
  fetchFrigateCameras,
  getFrigateSnapshotUrl,
  getFrigateStreamUrl,
  getFrigateClipUrl,
  postCameraPtz,
  postCameraLight,
  postCameraSpeak,
  postCameraWake,
  fetchFrigateEvents,
  fetchCameraAlerts,
  healHouseholdService,
  sendFissalChat,
  getServerUrl
} from '../services/api';
import { triggerHaptic } from '../services/purrSynthesizer';
import { NotificationSettingsModal } from '../components/NotificationSettingsModal';
import {
  Video,
  RefreshCw,
  Lightbulb,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Sparkles,
  Clock,
  ExternalLink,
  Radio,
  Camera,
  LayoutGrid,
  Maximize2,
  Zap,
  Power,
  Sliders,
  Volume2,
  VolumeX,
  Siren,
  Mic,
  Send,
  Bell,
  ShieldAlert,
  Play,
  Image as ImageIcon,
  Wrench,
  Activity,
  X,
  ZoomIn,
  ZoomOut,
  CheckCircle2
} from 'lucide-react';

const VOICE_INTERCOM_PRESETS = [
  { label: '👋 Greeting', text: 'Hello! We see you at the camera. How can we help you?' },
  { label: '📦 Delivery', text: 'Thank you! You can leave the package right by the door.' },
  { label: '🐕 Call Dogs', text: 'Come on pups, back to the porch! Good dogs!' },
  { label: '🚨 Warning', text: 'Attention: This property is monitored by live security cameras. Please step back.' }
];

export const FamilyCamerasView: React.FC = () => {
  const {
    status,
    isOnline: isConnected,
    latencyMs,
    setActiveTab,
    selectedCameraId,
    activeEventId: selectedEventId,
    setActiveEventId,
    activeAlertPayload,
    setActiveAlertPayload,
    authUser,
    profile: activeProfile
  } = useApp();

  const clearCameraDeepLink = () => {
    setActiveAlertPayload(null);
    setActiveEventId(null);
  };

  const [cameras, setCameras] = useState<CameraInfo[]>([]);
  const [activeCamId, setActiveCamId] = useState<string>('e-roto_1');
  const [streamMode, setStreamMode] = useState<'live' | 'snapshot'>('live');
  const [displayMode, setDisplayMode] = useState<'stage' | 'grid'>('stage');
  const [cacheBuster, setCacheBuster] = useState<number>(Date.now());
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Hardware Control States
  const [ptzDispatching, setPtzDispatching] = useState<boolean>(false);
  const [lightState, setLightState] = useState<'on' | 'off'>('off');
  const [lightDispatching, setLightDispatching] = useState<boolean>(false);
  const [sirenActive, setSirenActive] = useState<boolean>(false);
  const [sirenDispatching, setSirenDispatching] = useState<boolean>(false);
  const [intercomOpen, setIntercomOpen] = useState<boolean>(false);
  const [intercomText, setIntercomText] = useState<string>('');
  const [intercomDispatching, setIntercomDispatching] = useState<boolean>(false);
  const [isListeningVoice, setIsListeningVoice] = useState<boolean>(false);
  const [hardwareFeedback, setHardwareFeedback] = useState<string | null>(null);
  const [waking, setWaking] = useState<boolean>(false);

  // Notifications & Triggered Alert Inspector State
  const [notifModalOpen, setNotifModalOpen] = useState<boolean>(false);
  const [alertMediaMode, setAlertMediaMode] = useState<'snapshot' | 'clip'>('snapshot');
  const [recentAlerts, setRecentAlerts] = useState<TriggeredAlertPayload[]>([]);

  // Detections & Events state
  const [events, setEvents] = useState<FrigateEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState<boolean>(false);

  // Property Assistant & Self-Healing State
  const [assistantLoading, setAssistantLoading] = useState<boolean>(false);
  const [assistantReply, setAssistantReply] = useState<string | null>(null);
  const [customAskInput, setCustomAskInput] = useState<string>('');
  const [healingService, setHealingService] = useState<string | null>(null);

  // Sync camera selection when deep-linked from a notification
  useEffect(() => {
    if (selectedCameraId && cameras.some(c => c.id === selectedCameraId)) {
      setActiveCamId(selectedCameraId);
      setDisplayMode('stage');
    }
  }, [selectedCameraId, cameras]);

  // If selectedEventId was set without activeAlertPayload, match it from events or create payload
  useEffect(() => {
    if (selectedEventId && !activeAlertPayload) {
      const matchedEv = events.find(e => e.id === selectedEventId);
      if (matchedEv) {
        setActiveAlertPayload({
          id: matchedEv.id,
          eventId: matchedEv.id,
          title: `🚨 ${matchedEv.label.toUpperCase()} Detected — ${matchedEv.camera}`,
          body: `Confidence ${Math.round((matchedEv.top_score || matchedEv.score || 0.85) * 100)}%`,
          camera: matchedEv.camera,
          label: matchedEv.label,
          zone: matchedEv.zones?.join(', '),
          timestamp: new Date(matchedEv.start_time * 1000).toISOString(),
          snapshotUrl: getFrigateSnapshotUrl(matchedEv.id),
          clipUrl: getFrigateClipUrl(matchedEv.id)
        });
      } else {
        setActiveAlertPayload({
          id: selectedEventId,
          eventId: selectedEventId,
          title: `🚨 Camera Alert Triggered`,
          body: `Event ID: ${selectedEventId}`,
          camera: selectedCameraId || activeCamId,
          snapshotUrl: getFrigateSnapshotUrl(selectedEventId),
          clipUrl: getFrigateClipUrl(selectedEventId)
        });
      }
    }
  }, [selectedEventId, events, activeAlertPayload, selectedCameraId, activeCamId, setActiveAlertPayload]);

  // Initial load + visibility-gated snapshot refresh (pauses in background or when in live stream mode)
  useEffect(() => {
    loadCameras();
    loadEvents();

    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (streamMode === 'snapshot' || displayMode === 'grid') {
        setCacheBuster(Date.now());
      }
    }, 6000);
    return () => clearInterval(interval);
  }, [streamMode, displayMode]);

  const showFeedback = (msg: string) => {
    setHardwareFeedback(msg);
    setTimeout(() => {
      setHardwareFeedback(prev => (prev === msg ? null : prev));
    }, 5500);
  };

  const loadCameras = async () => {
    setRefreshing(true);
    const cams = await fetchFrigateCameras();
    if (cams.length > 0) {
      setCameras(cams);
      if (selectedCameraId && cams.some(c => c.id === selectedCameraId)) {
        setActiveCamId(selectedCameraId);
      } else {
        const current = cams.find(c => c.id === activeCamId);
        const firstOnline = cams.find(c => c.status === 'online');
        if (!current || (current.status !== 'online' && firstOnline)) {
          setActiveCamId(firstOnline ? firstOnline.id : cams[0].id);
        }
      }
      const chosen = cams.find(c => c.id === activeCamId) || cams[0];
      if (chosen?.light_state) {
        setLightState(chosen.light_state);
      }
    }
    setRefreshing(false);
  };

  const loadEvents = async () => {
    setEventsLoading(true);
    const [evRes, alertRes] = await Promise.all([
      fetchFrigateEvents(undefined, 12),
      fetchCameraAlerts()
    ]);
    if (evRes.ok) {
      setEvents(evRes.events);
    }
    if (alertRes.ok) {
      setRecentAlerts(alertRes.alerts);
    }
    setEventsLoading(false);
  };

  const activeCam = cameras.find(c => c.id === activeCamId) || cameras.find(c => c.status === 'online') || cameras[0] || {
    id: 'e-roto_1',
    name: 'Dogshop - Porch PTZ',
    zone: 'Dogshop Porch & Yard',
    ptz: true,
    light: true,
    speaker: true,
    presets: ['house', 'driveway (renters)', 'driveway (main)', 'dogshop door'],
    status: 'online',
    model: 'Tapo TCW30 Outdoor PTZ',
    ip: '192.168.70.103',
    fps: 5
  };

  // Sync light state when switching active camera
  useEffect(() => {
    if (activeCam?.light_state) {
      setLightState(activeCam.light_state);
    }
  }, [activeCamId]);

  const handleWake = async () => {
    if (!isBatteryCam) return;
    triggerHaptic([25, 50]);
    setWaking(true);
    try {
      const res = await postCameraWake(activeCam.id);
      showFeedback(res.ok ? (res.payload || 'Wake probe sent to camera.') : (res.error || 'Wake signal dispatched.'));
      setTimeout(() => {
        loadCameras();
        setCacheBuster(Date.now());
      }, 2500);
    } catch (e: any) {
      showFeedback(e.message || 'Wake probe failed');
    } finally {
      setWaking(false);
    }
  };

  const handlePtz = async (
    action: 'up' | 'down' | 'left' | 'right' | 'zoom_in' | 'zoom_out' | 'home' | 'stop' | 'preset',
    presetName?: string
  ) => {
    if (!activeCam.ptz || ptzDispatching) return;
    triggerHaptic(20);
    setPtzDispatching(true);
    try {
      if (action === 'home') {
        const homePreset =
          activeCam.presets?.find(p => p.toLowerCase().includes('house') || p.toLowerCase().includes('home')) ||
          (activeCam.presets && activeCam.presets.length > 0 ? activeCam.presets[0] : null);
        if (homePreset) {
          await postCameraPtz(activeCam.id, { action: 'preset', preset: homePreset });
          showFeedback(`Moved ${activeCam.name} to preset: ${homePreset}`);
        } else {
          await postCameraPtz(activeCam.id, { action: 'stop' });
        }
      } else if (action === 'preset' && presetName) {
        await postCameraPtz(activeCam.id, { action: 'preset', preset: presetName });
        showFeedback(`Moving to preset: ${presetName}`);
      } else if (action === 'stop') {
        await postCameraPtz(activeCam.id, { action: 'stop' });
      } else {
        await postCameraPtz(activeCam.id, { action: 'step', direction: action as any });
      }
      setTimeout(() => setCacheBuster(Date.now()), 1000);
    } catch (err) {
      console.warn('PTZ error:', err);
    } finally {
      setPtzDispatching(false);
    }
  };

  const handleToggleLight = async (targetCamId?: string) => {
    const camId = targetCamId || activeCam.id;
    if (lightDispatching) return;
    triggerHaptic(25);
    setLightDispatching(true);
    const targetState = lightState === 'on' ? 'off' : 'on';
    try {
      const res = await postCameraLight(camId, targetState);
      setLightState(targetState);
      showFeedback(
        res.ok
          ? `💡 Floodlight turned ${targetState.toUpperCase()} on ${camId}`
          : `💡 Floodlight signal (${targetState.toUpperCase()}) sent to ${camId}`
      );
      setTimeout(() => setCacheBuster(Date.now()), 1200);
    } catch (err) {
      showFeedback('Could not toggle floodlight right now.');
    } finally {
      setLightDispatching(false);
    }
  };

  const handleToggleSiren = async (targetAction?: 'siren' | 'stop', targetCamId?: string) => {
    const camId = targetCamId || activeCam.id;
    if (sirenDispatching) return;
    triggerHaptic([40, 60, 40]);
    setSirenDispatching(true);
    const nextAction = targetAction || (sirenActive ? 'stop' : 'siren');
    try {
      const res = await postCameraSpeak(camId, nextAction);
      const isNowActive = nextAction === 'siren';
      setSirenActive(isNowActive);
      showFeedback(
        isNowActive
          ? `🚨 Deterrent Siren & Spotlight triggered on ${camId}!`
          : `🔇 Deterrent Siren silenced on ${camId}.`
      );
      if (res.ok && isNowActive) {
        setLightState('on');
      }
    } catch {
      showFeedback('Failed to reach camera siren controller.');
    } finally {
      setSirenDispatching(false);
    }
  };

  const handleBroadcastVoice = async (textToSpeak?: string) => {
    const phrase = (textToSpeak ?? intercomText).trim();
    if (!phrase || intercomDispatching) return;
    triggerHaptic(25);
    setIntercomDispatching(true);
    try {
      const res = await postCameraSpeak(activeCam.id, 'speak', phrase);
      showFeedback(
        res.ok
          ? `🎙️ Broadcasted over ${activeCam.name}: "${phrase}"`
          : `🎙️ Sent intercom audio to ${activeCam.name}: "${phrase}"`
      );
      if (!textToSpeak) setIntercomText('');
    } catch {
      showFeedback('Could not transmit voice broadcast.');
    } finally {
      setIntercomDispatching(false);
    }
  };

  const handleVoiceMicDictation = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showFeedback('Use the keyboard microphone key to dictate your voice message.');
      return;
    }
    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      setIsListeningVoice(true);
      triggerHaptic(20);
      recognition.onresult = (event: any) => {
        const transcript = event.results?.[0]?.[0]?.transcript || '';
        if (transcript) {
          setIntercomText(transcript);
          handleBroadcastVoice(transcript);
        }
        setIsListeningVoice(false);
      };
      recognition.onerror = () => setIsListeningVoice(false);
      recognition.onend = () => setIsListeningVoice(false);
      recognition.start();
    } catch {
      setIsListeningVoice(false);
    }
  };

  const handleQuickHeal = async (service: 'cameras' | 'photos' | 'network' | 'all', label: string) => {
    if (healingService) return;
    triggerHaptic([25, 40]);
    setHealingService(service);
    setAssistantReply(`🛠️ Fissal is healing ${label}...`);
    try {
      const res = await healHouseholdService(service);
      if (res.ok) {
        setAssistantReply(`✅ ${res.message || `${label} refreshed and verified healthy!`}`);
        loadCameras();
        loadEvents();
      } else {
        setAssistantReply(`⚠️ Self-heal notice: ${res.error || 'Could not complete automatic repair.'}`);
      }
    } catch (e: any) {
      setAssistantReply(`⚠️ Error running self-heal: ${e.message}`);
    } finally {
      setHealingService(null);
    }
  };

  const handlePropertyQuestion = async (query: string) => {
    if (!query.trim() || assistantLoading) return;
    triggerHaptic(20);
    setAssistantLoading(true);
    setAssistantReply(null);
    try {
      const res = await sendFissalChat(query, [], activeProfile);
      setAssistantReply(res.answer || 'Fissal checked the perimeter and all is calm.');
      setCustomAskInput('');
    } catch {
      setAssistantReply('Fissal could not reach the perimeter sensors right now. Try tapping "Fix Cameras" or "Fix Network".');
    } finally {
      setAssistantLoading(false);
    }
  };

  const handleInspectEvent = (ev: FrigateEvent) => {
    triggerHaptic(20);
    setAlertMediaMode('snapshot');
    setActiveAlertPayload({
      id: ev.id,
      eventId: ev.id,
      title: `${getDetectionIcon(ev.label)} ${ev.label.toUpperCase()} Detected — ${ev.camera}`,
      body: `Confidence: ${Math.round((ev.top_score || ev.score || 0.85) * 100)}% · Zone: ${ev.zones?.join(', ') || 'Perimeter'}`,
      camera: ev.camera,
      label: ev.label,
      zone: ev.zones?.join(', ') || 'Perimeter',
      timestamp: new Date(ev.start_time * 1000).toISOString(),
      snapshotUrl: getFrigateSnapshotUrl(ev.id),
      clipUrl: getFrigateClipUrl(ev.id)
    });
  };

  const getDetectionIcon = (label: string) => {
    switch ((label || '').toLowerCase()) {
      case 'person': return '🚶';
      case 'dog': return '🐕';
      case 'cat': return '🐈';
      case 'car':
      case 'vehicle': return '🚗';
      case 'package': return '📦';
      default: return '🚨';
    }
  };

  const formatEventTime = (timestamp: number | string) => {
    const d = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
    if (isNaN(d.getTime())) return 'Just now';
    const now = Date.now();
    const diffSec = Math.floor((now - d.getTime()) / 1000);
    if (diffSec < 60) return `${Math.max(1, diffSec)}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isBatteryCam = Boolean(activeCam.isBattery || activeCam.id === 'battery_roto_104');
  const isOnline = activeCam.status === 'online' && (activeCam.fps || 0) > 0;
  const isCamStandby = activeCam.status === 'standby' || activeCam.fps === 0;
  const onlineCameraCount = cameras.filter(c => c.status === 'online').length || 5;
  const dockerList = Array.isArray(status?.docker) ? status.docker : (status?.docker?.containers || []);
  const immichOnline = dockerList.length > 0 ? dockerList.some((d: any) => d.name?.includes('immich') && d.state === 'running') : true;

  // Resolve full URLs for activeAlertPayload
  const resolveAlertSnapshotUrl = (alert: TriggeredAlertPayload) => {
    const raw = alert.snapshotUrl || alert.imageUrl;
    if (raw) {
      if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
      return `${getServerUrl()}${raw.startsWith('/') ? '' : '/'}${raw}`;
    }
    return getFrigateSnapshotUrl(alert.eventId || alert.camera || activeCam.id, cacheBuster);
  };

  const resolveAlertClipUrl = (alert: TriggeredAlertPayload) => {
    if (alert.clipUrl) {
      if (alert.clipUrl.startsWith('http://') || alert.clipUrl.startsWith('https://')) return alert.clipUrl;
      return `${getServerUrl()}${alert.clipUrl.startsWith('/') ? '' : '/'}${alert.clipUrl}`;
    }
    if (alert.eventId) {
      return getFrigateClipUrl(alert.eventId);
    }
    return null;
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-y-auto p-3.5 space-y-3.5 pb-28 bg-[#070a0f]">
      {/* 1. AT-A-GLANCE HOUSEHOLD & PERIMETER STATUS CARD */}
      <div className="p-3 rounded-2xl bg-gradient-to-r from-slate-900/95 via-[#0d131f] to-slate-900/95 border border-white/10 shadow-lg space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-emerald-400 shadow-[0_0_8px_#34d399]' : 'bg-rose-400 animate-ping'}`} />
            <div>
              <h2 className="text-xs font-bold text-white tracking-wide flex items-center gap-1.5">
                <span>Household At-A-Glance</span>
                <span className="text-[10px] font-mono font-normal text-amber-300/90 px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20">
                  {authUser?.displayName || 'Family'}
                </span>
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                triggerHaptic(15);
                setNotifModalOpen(true);
              }}
              className="px-2.5 py-1 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 text-[10px] font-mono font-bold flex items-center gap-1 transition-all"
              title="Configure Camera Alert Notifications"
            >
              <Bell className="w-3 h-3" />
              <span>Alerts</span>
            </button>
            <button
              onClick={() => {
                triggerHaptic(15);
                setActiveTab('devices');
              }}
              className="px-2.5 py-1 rounded-xl bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/30 text-sky-300 text-[10px] font-mono font-bold flex items-center gap-1 transition-all"
              title="Open Deep Network Diagnostics & Self-Heal"
            >
              <Activity className="w-3 h-3" />
              <span>Net & Fix</span>
            </button>
          </div>
        </div>

        {/* 4 At-A-Glance Status Pills */}
        <div className="grid grid-cols-4 gap-1.5">
          <button
            onClick={() => setActiveTab('devices')}
            className="p-2 rounded-xl bg-black/40 border border-white/5 hover:border-emerald-500/30 text-left transition-all"
          >
            <div className="text-[9px] font-mono text-slate-400 uppercase">Network</div>
            <div className={`text-[11px] font-bold font-mono flex items-center gap-1 mt-0.5 ${isConnected ? 'text-emerald-400' : 'text-rose-400'}`}>
              <span>{isConnected ? 'ONLINE' : 'CHECK'}</span>
              {latencyMs > 0 && <span className="text-[9px] text-slate-400">{latencyMs}ms</span>}
            </div>
          </button>

          <button
            onClick={() => setDisplayMode(displayMode === 'grid' ? 'stage' : 'grid')}
            className="p-2 rounded-xl bg-black/40 border border-white/5 hover:border-amber-500/30 text-left transition-all"
          >
            <div className="text-[9px] font-mono text-slate-400 uppercase">Cameras</div>
            <div className="text-[11px] font-bold font-mono text-amber-300 mt-0.5">
              {onlineCameraCount}/{cameras.length || 5} Live
            </div>
          </button>

          <button
            onClick={() => setActiveTab('photos')}
            className="p-2 rounded-xl bg-black/40 border border-white/5 hover:border-sky-500/30 text-left transition-all"
          >
            <div className="text-[9px] font-mono text-slate-400 uppercase">Photos</div>
            <div className={`text-[11px] font-bold font-mono mt-0.5 ${immichOnline ? 'text-sky-300' : 'text-amber-400'}`}>
              {immichOnline ? 'Ready' : 'Standby'}
            </div>
          </button>

          <button
            onClick={() => setActiveTab('fissal')}
            className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/25 hover:bg-amber-500/20 text-left transition-all"
          >
            <div className="text-[9px] font-mono text-amber-300/80 uppercase">Fissal AI</div>
            <div className="text-[11px] font-bold font-mono text-amber-300 mt-0.5 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              <span>Ask / Fix</span>
            </div>
          </button>
        </div>
      </div>

      {/* 2. TRIGGERED ALERT INSPECTOR (Shows when a notification is tapped or an event is selected) */}
      {activeAlertPayload && (
        <div className="p-3.5 rounded-2xl bg-gradient-to-br from-rose-950/60 via-slate-900/95 to-black border-2 border-rose-500/50 shadow-2xl space-y-3 animate-in fade-in">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center text-rose-400 shrink-0">
                <ShieldAlert className="w-4 h-4" />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 text-[9px] font-mono font-bold uppercase border border-rose-500/30">
                    Triggered Alert
                  </span>
                  {activeAlertPayload.timestamp && (
                    <span className="text-[10px] font-mono text-slate-400">
                      {formatEventTime(activeAlertPayload.timestamp)}
                    </span>
                  )}
                </div>
                <h3 className="text-xs font-bold text-white mt-0.5">{activeAlertPayload.title}</h3>
              </div>
            </div>

            <button
              onClick={() => {
                triggerHaptic(15);
                clearCameraDeepLink();
              }}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all"
              title="Close Alert Inspector"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {activeAlertPayload.body && (
            <p className="text-xs text-slate-200 bg-black/40 p-2.5 rounded-xl border border-white/10 leading-relaxed">
              {activeAlertPayload.body}
            </p>
          )}

          {/* Alert Snapshot / Video Clip Viewport */}
          <div className="relative rounded-xl overflow-hidden bg-black border border-white/15 aspect-video flex items-center justify-center">
            {alertMediaMode === 'clip' && resolveAlertClipUrl(activeAlertPayload) ? (
              <video
                src={resolveAlertClipUrl(activeAlertPayload)!}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain"
                onError={() => {
                  showFeedback('Video clip still finalizing on NVR — showing high-res alert snapshot.');
                  setAlertMediaMode('snapshot');
                }}
              />
            ) : (
              <img
                src={resolveAlertSnapshotUrl(activeAlertPayload)}
                alt={activeAlertPayload.title}
                className="w-full h-full object-contain"
                onError={(e) => {
                  const fallbackCam = activeAlertPayload.camera || activeCam.id;
                  (e.target as HTMLImageElement).src = getFrigateSnapshotUrl(fallbackCam, cacheBuster);
                }}
              />
            )}

            {/* Toggle Snapshot vs Video Clip */}
            <div className="absolute top-2 right-2 flex items-center bg-black/80 backdrop-blur-md rounded-xl p-0.5 border border-white/15">
              <button
                onClick={() => setAlertMediaMode('snapshot')}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold flex items-center gap-1 transition-all ${
                  alertMediaMode === 'snapshot'
                    ? 'bg-amber-500/30 text-amber-300 border border-amber-500/40'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <ImageIcon className="w-3 h-3" />
                <span>Alert Photo</span>
              </button>
              {resolveAlertClipUrl(activeAlertPayload) && (
                <button
                  onClick={() => setAlertMediaMode('clip')}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold flex items-center gap-1 transition-all ${
                    alertMediaMode === 'clip'
                      ? 'bg-rose-500/30 text-rose-300 border border-rose-500/40'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Play className="w-3 h-3" />
                  <span>Play Video Clip</span>
                </button>
              )}
            </div>
          </div>

          {/* Quick Alert Response Buttons */}
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            {activeAlertPayload.camera && (
              <button
                onClick={() => {
                  triggerHaptic(20);
                  if (activeAlertPayload.camera) {
                    setActiveCamId(activeAlertPayload.camera);
                    setStreamMode('live');
                    setDisplayMode('stage');
                  }
                }}
                className="px-3 py-1.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 text-xs font-bold flex items-center gap-1.5 transition-all"
              >
                <Radio className="w-3.5 h-3.5" />
                <span>View Live Feed ({activeAlertPayload.camera})</span>
              </button>
            )}
            <button
              onClick={() => handleToggleLight(activeAlertPayload.camera || activeCam.id)}
              className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-amber-500/20 border border-white/15 text-slate-200 text-xs font-semibold flex items-center gap-1.5 transition-all"
            >
              <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
              <span>Floodlight</span>
            </button>
            <button
              onClick={() => handleToggleSiren('siren', activeAlertPayload.camera || activeCam.id)}
              className="px-3 py-1.5 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-300 text-xs font-bold flex items-center gap-1.5 transition-all"
            >
              <Siren className="w-3.5 h-3.5" />
              <span>Sound Siren</span>
            </button>
          </div>
        </div>
      )}

      {/* 3. CAMERA FLEET HEADER & SWITCHER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2 font-cinzel">
            <Video className="w-4 h-4 text-amber-400" />
            <span>Perimeter Cameras & Controls</span>
          </h2>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => {
              triggerHaptic(15);
              setDisplayMode(displayMode === 'stage' ? 'grid' : 'stage');
            }}
            className={`px-2.5 py-1.5 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-all ${
              displayMode === 'grid'
                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 shadow-sm'
                : 'bg-white/5 border-white/10 text-slate-300 hover:text-white'
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            <span className="text-[11px] font-mono">{displayMode === 'grid' ? 'Single View' : 'All Cams'}</span>
          </button>

          <button
            onClick={() => {
              triggerHaptic(15);
              setCacheBuster(Date.now());
              loadCameras();
              loadEvents();
            }}
            disabled={refreshing}
            className="p-1.5 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:text-white transition-all disabled:opacity-50"
            title="Refresh cameras & events"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-amber-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Horizontal Camera Switcher Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
        {cameras.map((cam) => {
          const isActive = cam.id === activeCam.id;
          const camOnline = cam.status === 'online' && (cam.fps || 0) > 0;
          return (
            <button
              key={cam.id}
              onClick={() => {
                triggerHaptic(20);
                setActiveCamId(cam.id);
                setCacheBuster(Date.now());
                if (displayMode === 'grid') setDisplayMode('stage');
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-2 ${
                isActive
                  ? 'bg-amber-500/25 text-amber-300 border border-amber-500/50 shadow-sm font-bold'
                  : 'bg-white/5 text-slate-400 hover:text-white border border-white/5 hover:border-white/10'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full shrink-0 ${
                  camOnline ? 'bg-emerald-400' : 'bg-amber-400/80'
                }`}
              />
              <span className="truncate max-w-[130px]">
                {cam.name.replace('Dogshop - ', '').replace('Lycan - ', '')}
              </span>
              {cam.isBattery && <span className="text-[9px] text-amber-300 font-mono">🔋</span>}
              {cam.ptz && <span className="text-[9px] text-amber-400/80 font-mono">PTZ</span>}
            </button>
          );
        })}
      </div>

      {/* 4. STAGE OR GRID VIEWPORT */}
      {displayMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {cameras.map((cam) => {
            const camOnline = cam.status === 'online' && (cam.fps || 0) > 0;
            return (
              <div
                key={cam.id}
                onClick={() => {
                  triggerHaptic(20);
                  setActiveCamId(cam.id);
                  setDisplayMode('stage');
                }}
                className="group relative rounded-2xl overflow-hidden bg-black/80 border border-white/10 hover:border-amber-400/60 transition-all cursor-pointer shadow-lg aspect-video flex items-center justify-center"
              >
                <img
                  src={getFrigateSnapshotUrl(cam.id, cacheBuster)}
                  alt={cam.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="%23555" stroke-width="2"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
                  }}
                />
                <div className="absolute top-2 inset-x-2 flex items-center justify-between pointer-events-none">
                  <div className="px-2 py-0.5 rounded-lg bg-black/70 backdrop-blur-md border border-white/10 text-[10px] font-mono font-bold text-white flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${camOnline ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                    <span className="truncate max-w-[140px]">{cam.name}</span>
                  </div>
                  {cam.zone && (
                    <span className="px-1.5 py-0.5 rounded bg-black/70 text-[9px] font-mono text-slate-300 border border-white/10">
                      {cam.zone}
                    </span>
                  )}
                </div>
                <div className="absolute bottom-2 inset-x-2 flex items-center justify-between pointer-events-none">
                  <span className="px-1.5 py-0.5 rounded bg-black/70 text-[9px] font-mono text-emerald-300">
                    {cam.fps ? `${cam.fps} FPS` : camOnline ? 'Online' : 'Standby'}
                  </span>
                  <span className="text-[10px] text-white/90 bg-black/70 px-2 py-0.5 rounded flex items-center gap-1 font-mono">
                    <Maximize2 className="w-3 h-3" /> Open Controls
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Primary Video Stage */}
          <div className="relative rounded-2xl overflow-hidden bg-black border border-white/10 shadow-2xl aspect-video flex items-center justify-center">
            {isBatteryCam && !isOnline && streamMode === 'live' ? (
              <div className="flex flex-col items-center justify-center p-6 text-center space-y-2.5 bg-gradient-to-b from-slate-900/60 to-black w-full h-full">
                <div className="w-11 h-11 rounded-2xl bg-amber-500/10 border border-amber-400/30 flex items-center justify-center text-amber-400">
                  <Power className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-white">{activeCam.name} (Battery Standby)</h3>
                  <p className="text-[10px] text-slate-400 max-w-xs mt-0.5">
                    Sleeping to conserve battery. Wake sensor or view last snapshot.
                  </p>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={handleWake}
                    disabled={waking}
                    className="px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 transition-all disabled:opacity-50"
                  >
                    <Zap className="w-3.5 h-3.5" />
                    <span>{waking ? 'Waking...' : 'Wake Camera'}</span>
                  </button>
                  <button
                    onClick={() => setStreamMode('snapshot')}
                    className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-medium transition-all"
                  >
                    Last Snapshot
                  </button>
                </div>
              </div>
            ) : !isOnline && streamMode === 'live' ? (
              <div className="flex flex-col items-center justify-center p-6 text-center space-y-2.5 bg-gradient-to-b from-slate-900/60 to-black w-full h-full">
                <div className="w-11 h-11 rounded-2xl bg-sky-500/10 border border-sky-400/30 flex items-center justify-center text-sky-400">
                  <Radio className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-white">{activeCam.name}</h3>
                  <p className="text-[10px] text-slate-400 max-w-xs mt-1">
                    Reconnecting to RTSP feed ({activeCam.ip}). Tap below to view snapshot or heal cameras.
                  </p>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => setStreamMode('snapshot')}
                    className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-medium transition-all"
                  >
                    View Snapshot
                  </button>
                  <button
                    onClick={() => handleQuickHeal('cameras', 'Perimeter Cameras')}
                    className="px-3 py-1.5 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-bold flex items-center gap-1"
                  >
                    <Wrench className="w-3.5 h-3.5" />
                    <span>Heal Camera Stream</span>
                  </button>
                </div>
              </div>
            ) : streamMode === 'live' ? (
              <img
                src={getFrigateStreamUrl(activeCam.id, 5, 480)}
                alt={activeCam.name}
                className="w-full h-full object-contain"
                onError={() => {
                  setStreamMode('snapshot');
                }}
              />
            ) : (
              <img
                src={getFrigateSnapshotUrl(activeCam.id, cacheBuster)}
                alt={activeCam.name}
                className="w-full h-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="%23555" stroke-width="2"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
                }}
              />
            )}

            {/* Top Overlay Strip */}
            <div className="absolute top-2.5 left-2.5 right-2.5 flex items-center justify-between pointer-events-none">
              <div className="px-2.5 py-1 rounded-lg bg-black/70 backdrop-blur-md border border-white/10 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${!isCamStandby ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                <span className="text-[11px] font-bold text-white tracking-wide font-mono">{activeCam.name}</span>
              </div>

              <div className="flex items-center gap-1.5 pointer-events-auto">
                <div className="flex items-center bg-black/75 backdrop-blur-md rounded-xl p-0.5 border border-white/15">
                  <button
                    onClick={() => {
                      triggerHaptic(15);
                      setStreamMode('live');
                    }}
                    className={`px-2 py-0.5 rounded-lg text-[10px] font-mono font-bold flex items-center gap-1 transition-all ${
                      streamMode === 'live'
                        ? 'bg-rose-500/30 text-rose-300 border border-rose-500/50'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Radio className="w-2.5 h-2.5 text-rose-400" />
                    <span>LIVE</span>
                  </button>
                  <button
                    onClick={() => {
                      triggerHaptic(15);
                      setStreamMode('snapshot');
                      setCacheBuster(Date.now());
                    }}
                    className={`px-2 py-0.5 rounded-lg text-[10px] font-mono font-bold flex items-center gap-1 transition-all ${
                      streamMode === 'snapshot'
                        ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Camera className="w-2.5 h-2.5 text-amber-400" />
                    <span>SNAP</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom Overlay Strip */}
            <div className="absolute bottom-2 left-2.5 right-2.5 flex items-center justify-between pointer-events-none">
              <span className="px-2 py-0.5 rounded bg-black/70 backdrop-blur-md text-[10px] font-mono text-slate-300 border border-white/10">
                {activeCam.zone || activeCam.ip} · {activeCam.fps ? `${activeCam.fps} FPS` : 'Standby'}
              </span>
              <a
                href={`https://frigate.ech-o.net/#${activeCam.id}`}
                target="_blank"
                rel="noreferrer"
                className="pointer-events-auto px-2 py-0.5 rounded bg-black/70 backdrop-blur-md text-[10px] font-mono text-sky-300 border border-sky-500/30 hover:bg-sky-500/20 flex items-center gap-1 transition-all"
              >
                <span>Full NVR</span>
                <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </div>
          </div>

          {/* 5. QUICK HARDWARE ACTION BAR (FLASHLIGHT, TWO-WAY AUDIO INTERCOM, SIREN ALARM) */}
          <div className="grid grid-cols-3 gap-2">
            {/* Flashlight / Floodlight Toggle */}
            <button
              onClick={() => handleToggleLight()}
              disabled={lightDispatching}
              className={`p-2.5 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${
                lightState === 'on'
                  ? 'bg-amber-400/25 border-amber-400 text-amber-200 shadow-[0_0_15px_rgba(251,191,36,0.25)]'
                  : 'bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.07]'
              }`}
            >
              <Lightbulb className={`w-5 h-5 ${lightState === 'on' ? 'text-amber-300 fill-amber-300' : 'text-amber-400'}`} />
              <span className="text-[11px] font-bold">
                {lightDispatching ? 'Switching...' : lightState === 'on' ? 'Flashlight ON' : 'Flashlight'}
              </span>
              <span className="text-[9px] font-mono text-slate-400">
                {activeCam.light ? 'Floodlight LED' : 'ONVIF Aux'}
              </span>
            </button>

            {/* Two-Way Audio / Voice Intercom */}
            <button
              onClick={() => {
                triggerHaptic(20);
                setIntercomOpen(!intercomOpen);
              }}
              className={`p-2.5 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${
                intercomOpen
                  ? 'bg-sky-500/25 border-sky-400 text-sky-200 shadow-[0_0_15px_rgba(56,189,248,0.2)]'
                  : 'bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.07]'
              }`}
            >
              <Mic className={`w-5 h-5 ${intercomOpen ? 'text-sky-300 animate-pulse' : 'text-sky-400'}`} />
              <span className="text-[11px] font-bold">Two-Way Talk</span>
              <span className="text-[9px] font-mono text-slate-400">Voice Intercom</span>
            </button>

            {/* Siren / Alarm Deterrent */}
            <button
              onClick={() => handleToggleSiren()}
              disabled={sirenDispatching}
              className={`p-2.5 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${
                sirenActive
                  ? 'bg-rose-500/30 border-rose-400 text-rose-200 shadow-[0_0_18px_rgba(244,63,94,0.35)] animate-pulse'
                  : 'bg-white/[0.03] border-white/10 text-slate-300 hover:bg-rose-500/15 hover:border-rose-500/30'
              }`}
            >
              {sirenActive ? (
                <VolumeX className="w-5 h-5 text-rose-300" />
              ) : (
                <Siren className="w-5 h-5 text-rose-400" />
              )}
              <span className="text-[11px] font-bold">
                {sirenDispatching ? 'Sending...' : sirenActive ? 'Stop Alarm' : 'Sound Alarm'}
              </span>
              <span className="text-[9px] font-mono text-slate-400">
                {sirenActive ? 'Siren Active' : 'Siren + Strobe'}
              </span>
            </button>
          </div>

          {/* Hardware Action Feedback Toast */}
          {hardwareFeedback && (
            <div className="p-2.5 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-200 text-xs flex items-center justify-between gap-2 animate-in fade-in">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" />
                <span>{hardwareFeedback}</span>
              </div>
              <button onClick={() => setHardwareFeedback(null)} className="text-slate-400 hover:text-white">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* TWO-WAY AUDIO / VOICE INTERCOM PANEL */}
          {intercomOpen && (
            <div className="p-3.5 rounded-2xl bg-sky-950/30 border border-sky-500/30 space-y-3 animate-in fade-in">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-sky-400" />
                  <span className="text-xs font-bold text-white">
                    Two-Way Intercom — {activeCam.name}
                  </span>
                </div>
                <button
                  onClick={handleVoiceMicDictation}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold flex items-center gap-1 border transition-all ${
                    isListeningVoice
                      ? 'bg-rose-500/30 border-rose-400 text-rose-200 animate-pulse'
                      : 'bg-sky-500/20 border-sky-400/40 text-sky-300 hover:bg-sky-500/30'
                  }`}
                >
                  <Mic className="w-3 h-3" />
                  <span>{isListeningVoice ? 'Listening...' : 'Tap to Speak'}</span>
                </button>
              </div>

              {/* Quick Voice Broadcast Presets */}
              <div className="grid grid-cols-2 gap-1.5">
                {VOICE_INTERCOM_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => handleBroadcastVoice(preset.text)}
                    disabled={intercomDispatching}
                    className="p-2 rounded-xl bg-black/50 hover:bg-sky-500/20 border border-white/10 hover:border-sky-400/40 text-left transition-all active:scale-95"
                  >
                    <div className="text-[11px] font-bold text-sky-300">{preset.label}</div>
                    <div className="text-[10px] text-slate-400 truncate mt-0.5">{preset.text}</div>
                  </button>
                ))}
              </div>

              {/* Custom Intercom Input */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleBroadcastVoice();
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  value={intercomText}
                  onChange={(e) => setIntercomText(e.target.value)}
                  placeholder="Type or dictate message to speak over camera..."
                  className="flex-1 bg-black/60 border border-white/15 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-400"
                />
                <button
                  type="submit"
                  disabled={!intercomText.trim() || intercomDispatching}
                  className="px-3.5 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-slate-950 font-bold text-xs flex items-center gap-1.5 transition-all"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Speak</span>
                </button>
              </form>
            </div>
          )}

          {/* 6. PTZ DIRECTIONAL, ZOOM & PRESETS PANEL */}
          {activeCam.ptz && (
            <div className="p-3.5 rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-200 flex items-center gap-1.5 font-cinzel">
                  <Sliders className="w-4 h-4 text-amber-400" />
                  <span>PTZ Camera Aim & Zoom</span>
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handlePtz('zoom_out')}
                    disabled={ptzDispatching}
                    className="px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-[10px] font-mono flex items-center gap-1"
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-3 h-3 text-amber-400" />
                    <span>Zoom -</span>
                  </button>
                  <button
                    onClick={() => handlePtz('zoom_in')}
                    disabled={ptzDispatching}
                    className="px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-[10px] font-mono flex items-center gap-1"
                    title="Zoom In"
                  >
                    <ZoomIn className="w-3 h-3 text-amber-400" />
                    <span>Zoom +</span>
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                {/* Directional Pad */}
                <div className="flex items-center justify-center mx-auto sm:mx-0">
                  <div className="grid grid-cols-3 gap-1.5 w-32 h-32 p-1.5 rounded-2xl bg-black/50 border border-white/10 items-center justify-items-center">
                    <div />
                    <button
                      onClick={() => handlePtz('up')}
                      disabled={ptzDispatching}
                      className="w-9 h-9 rounded-xl bg-white/10 hover:bg-amber-500/25 hover:text-amber-300 border border-white/10 flex items-center justify-center text-slate-200 transition-all active:scale-90"
                    >
                      <ChevronUp className="w-5 h-5" />
                    </button>
                    <div />

                    <button
                      onClick={() => handlePtz('left')}
                      disabled={ptzDispatching}
                      className="w-9 h-9 rounded-xl bg-white/10 hover:bg-amber-500/25 hover:text-amber-300 border border-white/10 flex items-center justify-center text-slate-200 transition-all active:scale-90"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handlePtz('home')}
                      disabled={ptzDispatching}
                      className="w-9 h-9 rounded-xl bg-amber-500/15 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 flex items-center justify-center transition-all active:scale-90"
                      title="Home Position"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handlePtz('right')}
                      disabled={ptzDispatching}
                      className="w-9 h-9 rounded-xl bg-white/10 hover:bg-amber-500/25 hover:text-amber-300 border border-white/10 flex items-center justify-center text-slate-200 transition-all active:scale-90"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>

                    <div />
                    <button
                      onClick={() => handlePtz('down')}
                      disabled={ptzDispatching}
                      className="w-9 h-9 rounded-xl bg-white/10 hover:bg-amber-500/25 hover:text-amber-300 border border-white/10 flex items-center justify-center text-slate-200 transition-all active:scale-90"
                    >
                      <ChevronDown className="w-5 h-5" />
                    </button>
                    <div />
                  </div>
                </div>

                {/* Quick Presets */}
                {activeCam.presets && activeCam.presets.length > 0 ? (
                  <div className="flex-1 w-full sm:min-w-[200px] space-y-1.5">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block font-mono">
                      1-Tap Camera Angles
                    </span>
                    <div className="grid grid-cols-2 gap-1.5">
                      {activeCam.presets.map((pr) => (
                        <button
                          key={pr}
                          onClick={() => handlePtz('preset', pr)}
                          disabled={ptzDispatching}
                          className="px-2.5 py-2 rounded-xl bg-black/50 hover:bg-amber-500/20 border border-white/10 hover:border-amber-400/40 text-slate-200 hover:text-white text-xs capitalize truncate text-left transition-all active:scale-95 font-mono"
                        >
                          📍 {pr}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 w-full p-3 rounded-xl bg-black/30 border border-white/5 text-[11px] font-mono text-slate-400 text-center">
                    Use the directional pad to pan & tilt {activeCam.name.replace('Dogshop - ', '')}.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 7. COMMAND FISSAL: PROPERTY CHECK & SELF-HEALING */}
      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-amber-500/10 via-white/[0.02] to-transparent border border-amber-500/25 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-amber-300 flex items-center gap-1.5 font-cinzel">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span>Command Fissal (Check Property or Fix Issues)</span>
          </span>
          {(assistantLoading || healingService) && (
            <span className="text-[10px] text-amber-400 animate-pulse font-mono">Fissal working...</span>
          )}
        </div>

        {/* Quick Question & Self-Heal Chips */}
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => handlePropertyQuestion('Check all cameras and tell me if the dogs and yard look safe.')}
            disabled={assistantLoading}
            className="px-2.5 py-1.5 rounded-xl bg-black/50 hover:bg-amber-500/20 border border-white/10 hover:border-amber-500/30 text-slate-200 text-xs transition-all"
          >
            🐕 Check Dogs & Yard
          </button>
          <button
            onClick={() => handlePropertyQuestion('Has any car, person, or delivery been spotted on the driveway recently?')}
            disabled={assistantLoading}
            className="px-2.5 py-1.5 rounded-xl bg-black/50 hover:bg-amber-500/20 border border-white/10 hover:border-amber-500/30 text-slate-200 text-xs transition-all"
          >
            🚗 Driveway Activity?
          </button>
          <button
            onClick={() => handleQuickHeal('cameras', 'Cameras & NVR')}
            disabled={Boolean(healingService)}
            className="px-2.5 py-1.5 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 text-xs font-semibold flex items-center gap-1 transition-all"
          >
            <Wrench className="w-3 h-3" />
            <span>Fix Cameras</span>
          </button>
          <button
            onClick={() => handleQuickHeal('all', 'All Household Services')}
            disabled={Boolean(healingService)}
            className="px-2.5 py-1.5 rounded-xl bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/30 text-sky-300 text-xs font-semibold flex items-center gap-1 transition-all"
          >
            <Wrench className="w-3 h-3" />
            <span>Fix Network & Photos</span>
          </button>
        </div>

        {/* Inline Direct Command Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handlePropertyQuestion(customAskInput);
          }}
          className="flex items-center gap-2"
        >
          <input
            type="text"
            value={customAskInput}
            onChange={(e) => setCustomAskInput(e.target.value)}
            placeholder="Ask Fissal anything or tell Fissal what to fix..."
            className="flex-1 bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400/60"
          />
          <button
            type="submit"
            disabled={!customAskInput.trim() || assistantLoading}
            className="px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 font-bold text-xs flex items-center gap-1 transition-all"
          >
            <Send className="w-3.5 h-3.5" />
            <span>Send</span>
          </button>
        </form>

        {/* Assistant Reply Box */}
        {assistantReply && (
          <div className="p-3 rounded-xl bg-black/60 border border-amber-500/30 text-xs text-amber-100/95 leading-relaxed whitespace-pre-wrap animate-in fade-in">
            <div className="flex items-center justify-between mb-1">
              <span className="font-bold text-amber-400">🐾 Fissal Prime:</span>
              <button onClick={() => setAssistantReply(null)} className="text-slate-400 hover:text-white text-[10px]">
                Dismiss
              </button>
            </div>
            {assistantReply}
          </div>
        )}
      </div>

      {/* 8. RECENT CAMERA ALERTS & DETECTIONS (Tap any alert to view snapshot + video clip!) */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-slate-200 uppercase tracking-wider flex items-center gap-1.5 font-cinzel">
            <Clock className="w-3.5 h-3.5 text-amber-400" />
            <span>Recent Camera Alerts (Tap to View Photo & Clip)</span>
          </h3>
          <button
            onClick={loadEvents}
            disabled={eventsLoading}
            className="text-[11px] text-amber-400 hover:underline flex items-center gap-1 font-mono"
          >
            <RefreshCw className={`w-3 h-3 ${eventsLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Push Alert Cards if any recent push alerts exist */}
        {recentAlerts.length > 0 && (
          <div className="space-y-1.5">
            {recentAlerts.slice(0, 3).map((al) => (
              <div
                key={al.id}
                onClick={() => {
                  triggerHaptic(20);
                  setAlertMediaMode('snapshot');
                  setActiveAlertPayload(al);
                }}
                className="p-2.5 rounded-xl bg-rose-950/25 border border-rose-500/30 hover:border-rose-400/60 cursor-pointer transition-all flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <img
                    src={resolveAlertSnapshotUrl(al)}
                    alt={al.title}
                    className="w-14 h-10 rounded-lg object-cover bg-black border border-white/10 shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = getFrigateSnapshotUrl(al.camera || 'e-roto_1');
                    }}
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-rose-200 truncate">{al.title}</div>
                    <div className="text-[10px] text-slate-300 truncate">{al.body}</div>
                  </div>
                </div>
                <span className="px-2 py-1 rounded-lg bg-rose-500/20 text-rose-300 text-[10px] font-mono font-bold shrink-0">
                  View Alert
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Frigate NVR Event Detections with Snapshot Thumbnails */}
        {events.length === 0 ? (
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 text-center text-xs text-slate-500 font-mono">
            No recent detections across perimeter cameras.
          </div>
        ) : (
          <div className="space-y-1.5">
            {events.slice(0, 8).map((ev) => (
              <div
                key={ev.id}
                onClick={() => handleInspectEvent(ev)}
                className="p-2 rounded-xl bg-black/40 border border-white/10 hover:border-amber-400/50 cursor-pointer transition-all flex items-center justify-between gap-2.5"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="relative w-16 h-11 rounded-lg overflow-hidden bg-slate-900 border border-white/10 shrink-0">
                    <img
                      src={getFrigateSnapshotUrl(ev.id)}
                      alt={ev.label}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = getFrigateSnapshotUrl(ev.camera);
                      }}
                    />
                    <span className="absolute bottom-0.5 right-0.5 text-[10px] bg-black/70 px-1 rounded">
                      {getDetectionIcon(ev.label)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-white capitalize flex items-center gap-1.5">
                      <span>{ev.label} detected</span>
                      <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
                        {Math.round((ev.top_score || ev.score || 0.85) * 100)}%
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-400 flex items-center gap-1.5 font-mono truncate mt-0.5">
                      <span>{ev.camera}</span>
                      <span>·</span>
                      <span>{formatEventTime(ev.start_time)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="px-2 py-1 rounded-lg bg-white/5 hover:bg-amber-500/20 text-amber-300 text-[10px] font-mono border border-white/10 flex items-center gap-1">
                    <Play className="w-2.5 h-2.5" />
                    <span>Inspect</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification Settings Modal */}
      <NotificationSettingsModal isOpen={notifModalOpen} onClose={() => setNotifModalOpen(false)} />
    </div>
  );
};
